import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { GlobalVariable } from "../app.component";
import { DataService, Response } from "../dataService/data.service";

// Supported review languages: English, Tamil, Odia (Odisha), Marathi (Maharashtra).
type LangCode = "en" | "ta" | "or" | "mr";

interface ServiceItem {
  id?: number | string;
  title?: string;
  name?: string;
}

interface KeywordItem {
  id: string | null;
  text: string;
  display_order?: number;
}
interface WordPoolItem {
  id: string | null;
  text: string;
  keyword_id?: string | null;
  display_order?: number;
}
// Sentiment-bucketed sets the backend returns in full (positive + negative).
interface SentimentBuckets<T> {
  positive: T[];
  negative: T[];
}

// Single payload that lands the user directly on the final review page.
// `ai_review_enabled` decides whether `review_draft` is the AI-generated text
// (true) or a template-seeded positive review (false). The selection arrays
// reflect what was chosen earlier in the WhatsApp flow.
interface GenBootstrapResponse {
  slug: string;
  business_name: string;
  outlet_name: string | null;
  business_logo: string | null;
  location: string | null;
  rating: number;
  google_review_url: string | null;
  vertical?: string; // optional business category, if the API supplies it

  ai_review_enabled: boolean;
  sentiment: "positive" | "negative";
  selected_keywords: string[];
  selected_words: string[];
  others_text: string;
  others_keyword?: string; // the free-text "Others" chip label to exclude
  services: ServiceItem[];

  // Outlet-configured welcome line shown on the consumer page (if set).
  consumer_welcome_message?: string;

  // Recipient's name from the review invite — drives the "Hello <name>!"
  // greeting on the sentiment screen. Absent for anonymous/QR slugs.
  contact_name?: string;

  // Full keyword/intent sets (all options, not just the selected ones) so the
  // page can render every chip and highlight the selected ones.
  keywords?: SentimentBuckets<KeywordItem>;
  word_pools?: SentimentBuckets<WordPoolItem>;

  // Language + pre-configured default messages per language (en/ta), so the
  // language toggle can switch the standard draft instantly without a refetch.
  language?: LangCode;
  supported_languages?: string[];
  default_messages?: { [lang: string]: string[] };

  // Final text to show in the textarea. For ai_review_enabled=false this is
  // the template positive review the backend pre-fills.
  review_draft: string;

  // ── Company-specific page copy (all optional; UI falls back to generic
  // defaults when absent). Lets each outlet — e.g. Muthoot Finance (gold
  // loans) vs a restaurant — speak in its own voice without forking the UI.
  welcome_message?: string; // hero blurb under the business name
  highlight_hint?: string; // sub-line under the "highlight" section title
  quick_add_phrases?: string[]; // minimal-mode "Quick add" starter phrases
}

@Component({
  selector: "app-ai-review-gen",
  templateUrl: "./ai-review-gen.component.html",
  styleUrls: ["./ai-review-gen.component.scss"],
})
export class AiReviewGenComponent implements OnInit, AfterViewChecked {
  private BASE_URL = GlobalVariable;

  @ViewChild("reviewArea") reviewArea?: ElementRef<HTMLTextAreaElement>;
  // Cached so we only resize when the text actually changed (autosize runs in
  // AfterViewChecked, which fires often).
  private lastSizedValue = " ";

  // The page accepts EITHER a plain slug (route param :id or ?slug=) OR an
  // encoded `data` blob (?data=). Whichever is present is forwarded to the
  // backend, which decodes/resolves it into the bootstrap payload.
  slug_value = "";
  data_value = "";

  // Optional ?vertical= override (e.g. "gold-loan", "restaurant", "clinic").
  // Used purely to pick company-appropriate page copy on the frontend until
  // the backend supplies welcome_message/highlight_hint/quick_add_phrases.
  verticalParam = "";

  // Optional ?use_ai= override of the outlet's default AI behaviour:
  //   "true"    → full AI flow: keyword chips + AI-generated draft + polish.
  //   "minimal" → no pre-filled draft; user types and can AI-polish their words.
  //   "false"   → no AI at all: blank box + guiding prompts, just type & copy.
  // When absent we fall back to the outlet default (config.ai_review_enabled).
  aiOverride: "true" | "plain" | "minimal" | null = null;

  // Active review language. Drives both the standard message shown and the
  // language the AI generates in.
  language: LangCode = "en";
  readonly languages: { code: LangCode; label: string }[] = [
    { code: "en", label: "English" },
    { code: "ta", label: "தமிழ்" },
    { code: "or", label: "ଓଡ଼ିଆ" },
    { code: "mr", label: "मराठी" },
  ];

  loading = true;
  loadError = false;
  // No slug/data in the URL at all → the link is invalid, not a transient
  // failure. Drives a distinct "no page" screen (nothing to retry).
  noSlug = false;

  // Sentiment gate shown before the AI review screen (mirrors the QR flow):
  //   thumbs   → 👍/👎 choice
  //   review   → the AI review screen (positive path)
  //   negative → the feedback form (negative path)
  //   thanks   → terminal thank-you after a negative submit
  // Outlets with AI disabled are never sent this link, but we still gate on
  // ai_review_enabled so a misrouted slug can't land on the AI screen.
  step: "thumbs" | "review" | "negative" | "thanks" = "thumbs";
  // True when the backend reports feedback was already recorded for this slug,
  // so the thank-you copy says "already received" instead of thanking afresh.
  feedbackAlreadyGiven = false;
  // Records the thumbs_up vote before revealing the review screen.
  thumbsBusy = false;

  config: GenBootstrapResponse | null = null;

  reviewDraft = "";
  // Flips once we've CONFIRMED the review is on the clipboard, so we can show
  // the thank-you + paste-and-submit instruction panel. Named "reviewCopied"
  // (not "posted") because we can only verify the copy, never the actual Google
  // submission — keeps funnel numbers honest.
  reviewCopied = false;
  // True only for devices where copy failed even after the fallback (e.g.
  // WhatsApp WebView). Drives the intermediate "copy it manually, then open
  // Google" screen so we never send them to an empty paste target.
  copyFailed = false;
  isRefining = false;
  isGenerating = false;
  isPolishing = false;

  // Client-side cap on "Polish" (minimal mode). Counts down; at 0 the button
  // disables. Resets on page reload — adequate for a low-abuse consumer page.
  // (For a hard, reload-proof limit the backend would have to enforce it per
  // slug/session and return 429.)
  readonly MAX_POLISHES = 3;
  polishesLeft = this.MAX_POLISHES;

  // Highlight chips selected on this page (seeded from selected_keywords +
  // selected_words, i.e. the keywords/intent picked earlier in the flow).
  selectedKeywords = new Set<string>();

  // Intents (word_pool) are no longer shown or chosen by the user. We pick a
  // small RANDOM subset of the linked intents internally (see splitSelection),
  // so the draft stays varied without exposing the intent UI.

  // Becomes true when the user changes a keyword selection after a draft already
  // exists, so we can nudge them to regenerate. Reset after a successful
  // generate/regenerate and after the initial bootstrap draft loads.
  selectionChangedSinceGenerate = false;

  // Snapshot of the keyword selection the CURRENT draft was generated from.
  // Lets "Keep as is" restore the chips to match the draft when the user
  // changed their selection but chose not to regenerate.
  private committedKeywords = new Set<string>();

  // One-time hint shown after the auto-generated bootstrap draft loads, letting
  // the user know the review was pre-filled from a seeded selection and can be
  // edited or regenerated. Dismissed on first edit or explicit close.
  showInitialDraftHint = false;

  // Generic fallback starter phrases (used when the outlet config doesn't
  // Frontend copy presets keyed by vertical. Until the backend supplies
  // per-outlet copy, we pick company-appropriate text from here based on the
  // resolved vertical (?vertical= param or config.vertical). "default" is the
  // generic fallback. Keys are matched loosely (see resolvedVertical()).
  private readonly COPY: {
    [k: string]: { welcome: string; hint: string; hints: string[] };
  } = {
    default: {
      welcome:
        "Thanks for visiting! We'd love to hear about your experience — your words help others discover us.",
      hint: "Tap what stood out — we'll write your review around it.",
      hints: ["Great service", "Friendly staff", "Highly recommend"],
    },
    "gold-loan": {
      welcome:
        "Thanks for choosing us for your gold loan! We'd love to hear how your experience went.",
      hint: "Tap what stood out — we'll build your review around it.",
      hints: ["Quick loan process", "Transparent charges", "Helpful staff"],
    },
    finance: {
      welcome:
        "Thanks for banking with us! Tell us how we did — your words help others choose with confidence.",
      hint: "Tap what stood out — we'll build your review around it.",
      hints: ["Smooth process", "Clear on charges", "Courteous staff"],
    },
    clinic: {
      welcome:
        "Thanks for visiting! We'd love to hear about your care experience — it helps others find us.",
      hint: "Tap what stood out — we'll write your review around it.",
      hints: ["Knowledgeable doctor", "Caring staff", "Clean & comfortable"],
    },
    restaurant: {
      welcome:
        "Thanks for dining with us! We'd love to hear what made your visit great.",
      hint: "Tap what stood out — we'll write your review around it.",
      hints: ["Delicious food", "Great ambience", "Friendly service"],
    },
  };

  // Resolve the active vertical: ?vertical= param wins, else config.vertical,
  // normalised to a COPY key (lower-cased, spaces/underscores → hyphens, and a
  // few common aliases mapped). Returns "default" when nothing matches.
  private resolvedVertical(): string {
    const raw = (this.verticalParam || this.config?.vertical || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, "-");
    if (!raw) return "default";
    if (this.COPY[raw]) return raw;
    if (raw.includes("gold")) return "gold-loan";
    if (raw.includes("loan") || raw.includes("bank") || raw.includes("financ"))
      return "finance";
    if (raw.includes("clinic") || raw.includes("hospital") || raw.includes("hair"))
      return "clinic";
    if (raw.includes("food") || raw.includes("restaurant") || raw.includes("cafe"))
      return "restaurant";
    return "default";
  }
  private get copy() {
    return this.COPY[this.resolvedVertical()];
  }

  // Precedence for all three: backend-supplied field > vertical preset > generic.
  get promptHints(): string[] {
    const c = this.config?.quick_add_phrases;
    return c && c.length ? c.slice(0, 3) : this.copy.hints;
  }
  get welcomeMessage(): string {
    return (
      this.config?.consumer_welcome_message ||
      this.config?.welcome_message ||
      this.copy.welcome
    );
  }
  get highlightHint(): string {
    return this.config?.highlight_hint || this.copy.hint;
  }

  constructor(
    private route: ActivatedRoute,
    private http: DataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // This app routes the slug as a PATH param (/ai-review/:id), unlike the
    // standalone review app which only ever receives ?slug=. Seed from the path
    // first; a ?slug=/?data= query param below still wins if one is present.
    const routeId = this.route.snapshot.paramMap.get("id");
    if (routeId) this.slug_value = routeId;

    // slug or encoded data arrive as query params: ?slug=... or ?data=...
    this.route.queryParams.forEach((qp) => {
      if (qp["slug"]) this.slug_value = qp["slug"];
      if (qp["data"]) this.data_value = qp["data"];
      if (qp["vertical"]) this.verticalParam = qp["vertical"];
      const ua = (qp["use_ai"] || "").toString().trim().toLowerCase();
      // Accept friendly aliases: "full"→true (full AI), "false"→plain.
      if (ua === "true" || ua === "full") this.aiOverride = "true";
      else if (ua === "minimal") this.aiOverride = "minimal";
      else if (ua === "plain" || ua === "false") this.aiOverride = "plain";
      // Language is English-only now (the toggle UI was removed). We keep
      // sending language:"en" in requests but ignore any ?language= override.
    });

    if (!this.slug_value && !this.data_value) {
      this.noSlug = true;
      this.loading = false;
      return;
    }
    this.bootstrap();
  }

  // Grow the textarea to fit its content (longer reviews → taller box) while
  // never shrinking below the CSS min-height. Runs after each CD pass but only
  // touches the DOM when the text changed.
  ngAfterViewChecked(): void {
    if (this.reviewArea && this.reviewArea.nativeElement.value !== this.lastSizedValue) {
      this.autosize();
    }
  }

  // Grow the textarea to fit content, capped at MAX_TEXTAREA_PX (~400 chars);
  // past the cap CSS max-height + overflow-y take over and it scrolls.
  private readonly MAX_TEXTAREA_PX = 230;
  autosize(): void {
    const el = this.reviewArea?.nativeElement;
    if (!el) return;
    this.lastSizedValue = el.value;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, this.MAX_TEXTAREA_PX)}px`;
  }

  // Base request body shared by all calls: slug or encoded data blob, plus the
  // vertical when supplied (?vertical=) so the backend can tailor its output.
  private baseBody(extra: { [k: string]: any } = {}): { [k: string]: any } {
    const body: { [k: string]: any } = this.data_value
      ? { data: this.data_value }
      : { slug: this.slug_value };
    if (this.verticalParam) body.vertical = this.verticalParam;
    body.language = this.language;
    return { ...body, ...extra };
  }

  private bootstrap(): void {
    this.loading = true;
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_GEN_BOOTSTRAP, this.baseBody())
      .subscribe(
        (res: Response) => {
          if (res.code === 200) {
            const data: GenBootstrapResponse =
              typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            this.config = data;
            // The backend resolves the real slug (even from the encoded data
            // blob); adopt it so refine/generate calls always have a slug.
            if (data.slug) this.slug_value = data.slug;
            // Pre-select the highlights chosen earlier in the flow — but only
            // those that exist in this outlet's KEYWORD set (chips are keywords
            // only), so stale/mismatched selections don't create phantom chips.
            const bucket =
              data.sentiment === "negative" ? "negative" : "positive";
            const validKeywords = (data.keywords?.[bucket] || []).map(
              (k) => k.text
            );
            const valid = new Set<string>(validKeywords);
            [
              ...(data.selected_keywords || []),
              ...(data.selected_words || []),
            ].forEach((k) => {
              if (valid.has(k)) this.selectedKeywords.add(k);
            });
            this.loadError = false;
            // No draft yet — the thumbs gate runs first. Generation is deferred
            // to pickThumbs("up") so we never spend an AI call on a customer
            // who is about to tap 👎.
          } else {
            this.loadError = true;
          }
          this.loading = false;
        },
        () => {
          this.loadError = true;
          this.loading = false;
        }
      );
  }

  // ── Sentiment gate ────────────────────────────────────────────
  // 👍 → record the vote, reveal the AI review screen and start generating.
  // 👎 → the negative feedback form; no AI call is ever made.
  pickThumbs(which: "up" | "down"): void {
    if (this.thumbsBusy) return;

    if (which === "down") {
      this.step = "negative";
      return;
    }

    this.step = "review";
    this.recordThumbs("thumbs_up");
    // Only the full AI flow needs a generated draft; minimal/plain start blank.
    if (this.mode === "full") this.fetchDraft();
  }

  // Fire-and-forget vote write. Never blocks the UI — a failed vote must not
  // stop the customer from leaving a review.
  private recordThumbs(thumbs: "thumbs_up" | "thumbs_down"): void {
    const slug = this.slug_value || this.config?.slug;
    if (!slug) return;
    this.http
      .putRequest({
        customApiUrl: this.BASE_URL.REVIEW_RATING_API,
        data: {
          slug,
          thumbs,
          faces: null,
          formal_rating: null,
          review_scale_type: "TWO_POINT_SCALE",
        },
      })
      .subscribe(
        () => {},
        () => {}
      );
  }

  // Customer tapped 👎 by mistake — back to the thumbs choice.
  onNegativeBack(): void {
    this.step = "thumbs";
  }

  onNegativeSubmitted(): void {
    this.feedbackAlreadyGiven = false;
    this.step = "thanks";
  }

  onFeedbackAlreadyGiven(): void {
    this.feedbackAlreadyGiven = true;
    this.step = "thanks";
  }

  // Fetch the AI review draft on its own (slow) call, after the UI has loaded.
  // Sends the (seeded) selection so the draft reflects the highlighted chips.
  private fetchDraft(): void {
    this.isGenerating = true;
    const extra = this.config
      ? {
          sentiment: this.config.sentiment,
          selected_keywords: this.selectedKeywordList(),
          others_text: this.config.others_text,
          random_intents: true, // backend picks the intent subset
        }
      : {};
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_GEN_DRAFT, this.baseBody(extra))
      .subscribe(
      (res: Response) => {
        if (res.code === 200) {
          const d = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
          this.reviewDraft = d.review_draft || "";
          this.selectionChangedSinceGenerate = false;
          this.committedKeywords = new Set(this.selectedKeywords);
          // Let the user know this draft was auto-generated for them.
          this.showInitialDraftHint = !!this.reviewDraft.trim();
        }
        this.isGenerating = false;
      },
      () => {
        this.isGenerating = false;
      }
    );
  }

  // ── Mode resolution ───────────────────────────────────────────
  // The query-param override wins; otherwise fall back to the outlet default.
  //   full    → AI keyword chips + generated draft + refine/generate/polish
  //   minimal → user types their own words, can AI-polish them
  //   plain   → user types, no AI at all
  get mode(): "full" | "minimal" | "plain" {
    // Outlet has AI off — never generate, whatever the URL asks for. These
    // outlets aren't sent this link at all, so this is purely a safety net for
    // a misrouted slug.
    if (this.config && !this.config.ai_review_enabled) return "plain";
    if (this.aiOverride === "true") return "full";
    if (this.aiOverride === "minimal") return "minimal";
    if (this.aiOverride === "plain") return "plain";
    // This route replaces the old always-AI 4-step flow, so default to the full
    // AI experience (chips + generated draft). ?use_ai=minimal|plain opts down.
    return "full";
  }

  // Any AI affordance is available unless we're in plain mode.
  get aiEnabled(): boolean {
    return this.mode !== "plain";
  }

  // ── View helpers ──────────────────────────────────────────────
  get charCount(): number {
    return this.reviewDraft.length;
  }

  // Business name is the headline; outlet name is the secondary line. When only
  // one is present it becomes the headline and the secondary line is hidden.
  get businessTitle(): string {
    return this.config?.business_name || this.config?.outlet_name || "";
  }

  get outletLine(): string | null {
    const b = this.config?.business_name;
    const o = this.config?.outlet_name;
    if (o && b && o !== b) return o;
    return null;
  }

  get logoInitial(): string {
    return (this.businessTitle || "?").charAt(0).toUpperCase();
  }

  // Once the user starts choosing (full mode + a topic picked), shrink the hero
  // to a slim header so the review + actions come into view without scrolling.
  get heroCollapsed(): boolean {
    return this.mode === "full" && this.selectedKeywords.size > 0;
  }

  // Sentiment bucket to read keyword/word_pool sets from.
  private get bucket(): "positive" | "negative" {
    return this.config?.sentiment === "negative" ? "negative" : "positive";
  }

  // Keyword option texts from bootstrap for the current sentiment, ordered by
  // the backend's `display_order` (ascending). Items without a display_order
  // fall to the end (treated as +Infinity); ties preserve API array order via
  // the stable sort. This makes chip order follow the admin-configured order
  // even if the transport ever reorders the array.
  private get keywordTexts(): string[] {
    const items = (this.config?.keywords?.[this.bucket] || []).slice();
    const order = (k: KeywordItem): number =>
      typeof k.display_order === "number" ? k.display_order : Number.POSITIVE_INFINITY;
    items.sort((a, b) => order(a) - order(b));
    return items.map((k) => k.text);
  }

  // Chip pool = the keyword options for the current sentiment only (not the
  // finer word_pool "intents"). Kept in the API's order — selected chips are
  // highlighted in place, not reordered. The free-text "Others" keyword is
  // dropped. De-duped.
  get keywordChips(): string[] {
    if (!this.config) return [];
    const others = (this.config.others_keyword || "Others").toLowerCase();
    return Array.from(new Set(this.keywordTexts)).filter(
      (k) => !!k && k.toLowerCase() !== others
    );
  }


  isKeywordSelected(kw: string): boolean {
    return this.selectedKeywords.has(kw);
  }

  toggleKeyword(kw: string): void {
    if (this.selectedKeywords.has(kw)) {
      this.selectedKeywords.delete(kw);
    } else {
      this.selectedKeywords.add(kw);
    }
    // Once a draft exists, any highlight change means the draft is stale — nudge
    // the user to regenerate with their new selection.
    if (this.reviewDraft.trim()) this.selectionChangedSinceGenerate = true;
    // Reselecting means they're acting on the draft — clear the intro hint.
    this.showInitialDraftHint = false;
  }

  // "Keep as is" — keep the current draft AND revert the chip selection back to
  // what that draft was generated from, so the highlights match the text again.
  dismissRegenNudge(): void {
    this.selectedKeywords = new Set(this.committedKeywords);
    this.selectionChangedSinceGenerate = false;
  }

  // Dismiss the one-time "we generated this for you" hint.
  dismissInitialDraftHint(): void {
    this.showInitialDraftHint = false;
  }

  // ── Selection → request ───────────────────────────────────────
  // Only the selected keyword texts are sent. Intents are no longer chosen here
  // — the backend's gen-draft picks a small random intent subset server-side
  // (via random_intents), so the consumer never sees or selects intents.
  private selectedKeywordList(): string[] {
    return Array.from(this.selectedKeywords);
  }

  // ── Refine / regenerate ───────────────────────────────────────
  refine(mode: "short" | "long" | "refine"): void {
    if (!this.reviewDraft.trim()) return;
    this.isRefining = true;
    this.http
      .postjson(
        this.BASE_URL.AI_REVIEW_REFINE,
        this.baseBody({ draft: this.reviewDraft, mode })
      )
      .subscribe(
        (res: Response) => {
          if (res.code === 200) this.reviewDraft = res.data["review_draft"];
          else this.toastr.error("Couldn't refine the review. Try again.");
          this.isRefining = false;
        },
        () => {
          this.toastr.error("Couldn't refine the review. Try again.");
          this.isRefining = false;
        }
      );
  }

  generateNew(): void {
    if (!this.config) return;
    this.isGenerating = true;
    // Regenerate via gen-draft (exclusive to this app) so the shared `generate`
    // endpoint stays untouched. Send only the keywords; the backend picks a
    // random intent subset (random_intents).
    this.http
      .postjson(
        this.BASE_URL.AI_REVIEW_GEN_DRAFT,
        this.baseBody({
          sentiment: this.config.sentiment,
          selected_keywords: this.selectedKeywordList(),
          others_text: this.config.others_text,
          random_intents: true,
        })
      )
      .subscribe(
        (res: Response) => {
          if (res.code === 200) {
            this.reviewDraft = res.data["review_draft"];
            this.selectionChangedSinceGenerate = false;
            this.committedKeywords = new Set(this.selectedKeywords);
          } else this.toastr.error("Couldn't generate the review. Try again.");
          this.isGenerating = false;
        },
        () => {
          this.toastr.error("Couldn't generate the review. Try again.");
          this.isGenerating = false;
        }
      );
  }

  // ── Language toggle ───────────────────────────────────────────
  setLanguage(lang: LangCode): void {
    if (lang === this.language) return;
    this.language = lang;
    if (this.mode === "full") {
      // AI flow → regenerate the draft in the new language.
      this.fetchDraft();
    } else if (this.reviewDraft.trim() === "" || this.isStandardDraft) {
      // Standard/empty draft → swap to the pre-translated default instantly.
      this.reviewDraft = this.standardDraft();
    }
  }

  isLanguage(lang: LangCode): boolean {
    return this.language === lang;
  }

  // Whether the current draft is one of the backend's standard default messages
  // (so a language switch can safely replace it). True until the user edits.
  private get isStandardDraft(): boolean {
    const dm = this.config?.default_messages || {};
    const all = ([] as string[]).concat(...Object.keys(dm).map((k) => dm[k] || []));
    return all.indexOf(this.reviewDraft.trim()) !== -1;
  }

  // Pick a stable default message for the active language from the bootstrap
  // bank (same deterministic index regardless of language so the toggle maps
  // 1:1 to the translated counterpart).
  private standardDraft(): string {
    const list = this.config?.default_messages?.[this.language] || [];
    if (!list.length) return "";
    const seed = this.slug_value || this.config?.slug || "";
    let sum = 0;
    for (let i = 0; i < seed.length; i++) sum += seed.charCodeAt(i);
    return list[sum % list.length];
  }

  // Minimal-mode AI assist: clean up the customer's own words on demand.
  // Reuses the refine endpoint with the "refine" mode.
  polish(): void {
    if (this.polishesLeft <= 0) {
      this.toastr.info("You've used all your AI polishes for now.");
      return;
    }
    if (!this.reviewDraft.trim()) {
      this.toastr.info("Write a few words first, then I'll polish them.");
      return;
    }
    this.isPolishing = true;
    this.http
      .postjson(
        this.BASE_URL.AI_REVIEW_REFINE,
        this.baseBody({ draft: this.reviewDraft, mode: "refine" })
      )
      .subscribe(
        (res: Response) => {
          if (res.code === 200) {
            this.reviewDraft = res.data["review_draft"];
            // Only count successful polishes against the cap.
            this.polishesLeft = Math.max(0, this.polishesLeft - 1);
          } else {
            this.toastr.error("Couldn't polish the text. Try again.");
          }
          this.isPolishing = false;
        },
        () => {
          this.toastr.error("Couldn't polish the text. Try again.");
          this.isPolishing = false;
        }
      );
  }

  // Tapping a starter phrase stacks it onto the draft as its own sentence so
  // the customer can build a review one tap at a time, then edit/polish.
  addHint(hint: string): void {
    let t = this.reviewDraft.trim();
    if (t && !/[.!?]$/.test(t)) t += ".";
    this.reviewDraft = t ? `${t} ${hint}.` : `${hint}.`;
  }

  // ── Copy & post ───────────────────────────────────────────────
  copyAndPostToGoogle(): void {
    const text = this.reviewDraft.trim();
    if (!text) {
      this.toastr.info("Nothing to copy yet");
      return;
    }

    const url = this.config?.google_review_url;

    // COPY FIRST, inside the tap gesture, and check the result before doing
    // anything else. Any navigation before the copy (e.g. the WhatsApp browser
    // escape) consumes the user-gesture activation and makes the clipboard write
    // fail. copyReview() tries the modern API, falls back to execCommand, and
    // confirms via read-back where possible.
    this.copyReview(this.reviewDraft).then((copied) => {
      if (!url) {
        if (copied) this.toastr.success("Review copied.");
        else this.showManualCopy();
        this.toastr.warning(
          "Google review link is not configured for this location."
        );
        return;
      }

      // Copy failed even after fallback → DON'T redirect. Show the intermediate
      // screen (long-press-to-copy + Open Google button).
      if (!copied) {
        this.showManualCopy();
        return;
      }

      // Copy confirmed → mark state, record the post, redirect after it settles.
      this.reviewCopied = true;
      this.copyFailed = false;
      this.toastr.success("Copied. Opening Google review page...");
      const go = () =>
        this.isWhatsAppBrowser()
          ? this.openInExternalBrowser(url)
          : window.location.replace(url);
      this.recordThenRedirect(go);
    });
  }

  // Show the intermediate manual-copy screen: review stays visible, user
  // long-presses to copy, then taps a separate button to open Google. Only
  // appears for devices where the programmatic copy genuinely failed.
  private showManualCopy(): void {
    this.copyFailed = true;
    this.reviewCopied = false;
    this.selectReviewText();
    this.toastr.error("Couldn't copy automatically — please copy it manually.");
  }

  // User confirmed they copied manually (from the intermediate screen). Trust
  // it, record, and open Google.
  openGoogleAfterManualCopy(): void {
    const url = this.config?.google_review_url;
    if (!url) {
      this.toastr.warning(
        "Google review link is not configured for this location."
      );
      return;
    }
    this.reviewCopied = true;
    const go = () =>
      this.isWhatsAppBrowser()
        ? this.openInExternalBrowser(url)
        : window.location.replace(url);
    this.recordThenRedirect(go);
  }

  // Focus + select the whole review so the OS long-press "Copy" is one tap away.
  private selectReviewText(): void {
    const el = this.reviewArea?.nativeElement;
    if (!el) return;
    el.focus();
    try {
      el.setSelectionRange(0, el.value.length);
    } catch {
      /* ignore */
    }
  }

  private isWhatsAppBrowser(): boolean {
    const ua = navigator.userAgent || "";
    return (
      /WhatsApp/i.test(ua) ||
      /\[WAiOS\//i.test(ua) ||
      /\[WABIZ\//i.test(ua) ||
      /WA4A\//i.test(ua)
    );
  }

  // Break out of WhatsApp's in-app webview into the system browser, where
  // clipboard paste and Google auth actually work.
  //
  // MUST be called synchronously inside the tap handler so iOS still treats it
  // as a user gesture.
  private openInExternalBrowser(url: string): void {
    if (!url) return;
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const stripped = url.replace(/^https?:\/\//, "");
    const fallback = encodeURIComponent(url);
    if (isAndroid) {
      window.location.href = `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
      return;
    }

    // iOS WhatsApp: window.location='x-safari-https://' is ignored on newer
    // WhatsApp builds. A REAL anchor click carries the user-gesture context
    // that iOS/WhatsApp is more willing to route out to Safari. Try the
    // x-safari scheme via the anchor first, then fall back to the plain URL.
    if (!this.clickAnchor(`x-safari-https://${stripped}`)) {
      this.clickAnchor(url);
    }
  }

  // Synthesize a real <a> element and click it within the current gesture.
  // Returns false if the element couldn't be created/clicked.
  private clickAnchor(href: string): boolean {
    try {
      const a = document.createElement("a");
      a.href = href;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.style.position = "fixed";
      a.style.opacity = "0";
      a.style.pointerEvents = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch {
      return false;
    }
  }

  // Copy the review and RESOLVE WITH WHETHER IT ACTUALLY LANDED.
  //   1. Try navigator.clipboard.writeText() (modern API), await it.
  //   2. On rejection / absence, fall back to execCommand("copy").
  //   3. Confirm via read-back where possible. A resolved writeText() and an
  //      execCommand() returning true both LIE in WebViews (WhatsApp), so only
  //      a read-back match counts as a confirmed success; when read-back is
  //      itself blocked (unverifiable) we trust the underlying call's result.
  private copyReview(text: string): Promise<boolean> {
    const clip: any = navigator.clipboard;
    const canAsync =
      !!clip && typeof clip.writeText === "function" && (window as any).isSecureContext;

    if (canAsync) {
      return clip.writeText(text).then(
        () =>
          this.verifyClipboard(text).then((verdict) => {
            if (verdict === "confirmed") return true;
            // Resolved but nothing landed → try the sync fallback once.
            if (verdict === "failed") return this.execCommandCopy(text);
            return true; // unverifiable → trust the resolve
          }),
        () => this.execCommandCopy(text)
      );
    }

    return Promise.resolve(this.execCommandCopy(text)).then((ok) =>
      this.verifyClipboard(text).then((verdict) =>
        verdict === "failed" ? false : ok
      )
    );
  }

  // Synchronous execCommand("copy") via a hidden textarea. Returns its boolean
  // result (which can still lie — the caller verifies via read-back).
  private execCommandCopy(text: string): boolean {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      /* best-effort */
    }
    document.body.removeChild(ta);
    return ok;
  }

  // THE DECISIVE CHECK: read the clipboard back and compare to what we wrote.
  // Resolves 'confirmed' (read-back matches) | 'failed' (readable but doesn't
  // match) | 'unverifiable' (read blocked/unsupported — e.g. WhatsApp WebView).
  private verifyClipboard(
    expected: string
  ): Promise<"confirmed" | "failed" | "unverifiable"> {
    const clip: any = navigator.clipboard;
    if (!clip || typeof clip.readText !== "function") {
      return Promise.resolve("unverifiable");
    }
    return clip.readText().then(
      (got: string) =>
        got === expected ? ("confirmed" as const) : ("failed" as const),
      () => "unverifiable" as const
    );
  }

  // Record the copy on the backend, then run `redirect` once the call settles —
  // in BOTH the success and error paths, so the API can never block or break the
  // navigation to Google. Backend field stays `posted_to_google` (no API change);
  // the frontend state is named reviewCopied for honesty.
  private recordThenRedirect(redirect: () => void): void {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      redirect();
    };
    const slug = this.slug_value || this.config?.slug;
    if (!slug) {
      go();
      return;
    }
    const body: { [k: string]: any } = {
      slug,
      sentiment: this.config?.sentiment || "positive",
      final_text: this.reviewDraft,
      posted_to_google: true,
    };
    try {
      this.http
        .postjson(this.BASE_URL.AI_REVIEW_CAPTURE, body)
        .subscribe(go, go);
    } catch (_e) {
      go();
    }
  }
}
